import React from 'react';
import { usePermissions } from '../hooks/usePermissions';

/**
 * Permission Gate Component
 * 
 * Conditionally renders children based on user permissions
 * 
 * Usage:
 * <PermissionGate permission="create_member">
 *   <button>Add Member</button>
 * </PermissionGate>
 * 
 * <PermissionGate permissions={['edit_member', 'delete_member']} requireAll>
 *   <button>Manage Member</button>
 * </PermissionGate>
 * 
 * <PermissionGate permission="view_members" fallback={<div>Access Denied</div>}>
 *   <MembersList />
 * </PermissionGate>
 */
export const PermissionGate = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children,
}) => {
  const { can, canAny, canAll, user } = usePermissions();

  // While user data is loading, don't render anything (or render children if you prefer)
  if (user === undefined || user === null) {
    return null; // or return <>{children}</> to show content while loading
  }

  let hasAccess = false;

  if (permission) {
    // Single permission check
    hasAccess = can(permission);
  } else if (permissions && Array.isArray(permissions)) {
    // Multiple permissions check
    hasAccess = requireAll ? canAll(permissions) : canAny(permissions);
  }

  if (!hasAccess) {
    return fallback;
  }

  return <>{children}</>;
};

/**
 * Permission Denied Message Component
 */
export const PermissionDenied = ({ message, className = '' }) => {
  return (
    <div className={`flex flex-col items-center justify-center p-8 ${className}`}>
      <div className="text-red-600 text-6xl mb-4">🔒</div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Access Denied
      </h2>
      <p className="text-gray-600 dark:text-gray-400 text-center max-w-md">
        {message || 'You don\'t have permission to access this resource. Please contact your administrator if you believe this is an error.'}
      </p>
    </div>
  );
};

/**
 * Page-level Permission Gate
 * Shows a full-page permission denied message if user doesn't have access
 * Shows loading state while permissions are being fetched
 */
export const PermissionPage = ({
  permission,
  permissions,
  requireAll = false,
  message,
  children,
}) => {
  const { user, loading } = usePermissions();
  
  // Show loading state while user data is being fetched
  if (loading || user === undefined) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <PermissionGate
      permission={permission}
      permissions={permissions}
      requireAll={requireAll}
      fallback={<PermissionDenied message={message} className="min-h-screen" />}
    >
      {children}
    </PermissionGate>
  );
};
